
import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Award, Users, Heart } from 'lucide-react';
import Navbar from '@/components/Navbar';
import PageTransition from '@/components/PageTransition';
import { AnimatedButton } from '@/components/AnimatedButton';
import { Logo } from '@/components/navbar/Logo';
import { Separator } from '@/components/ui/separator';

interface TeamMember {
  name: string;
  role: string;
  image: string;
  index: number;
}

const TeamCard: React.FC<TeamMember> = ({ name, role, image, index }) => {
  return (
    <div className="opacity-0 animate-fade-up" style={{ animationDelay: `${index * 100}ms` }}>
      <div className="aspect-square overflow-hidden rounded-xl mb-4">
        <img 
          src={image} 
          alt={name} 
          className="w-full h-full object-cover object-center object-top transition-transform hover:scale-105"
        />
      </div>
      <h3 className="text-lg font-semibold">{name}</h3>
      <p className="text-muted-foreground">{role}</p>
    </div>
  );
};

const About = () => {
  const team: TeamMember[] = [
    {
      name: "Michael Budd",
      role: "Co-Founder",
      image: "/lovable-uploads/b8d39d0e-5a80-4adf-8db5-cf431ed387ad.png",
      index: 0
    },
    {
      name: "Rob Ranson",
      role: "Co-Founder",
      image: "/lovable-uploads/f021dfd5-d05b-4467-b534-a925be73166f.png",
      index: 1
    }
  ];

  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        {/* Hero Section with Background Image */}
        <section className="relative h-[80vh] flex items-center justify-center bg-black">
          {/* Full-screen background image with overlay */}
          <div 
            className="absolute inset-0 z-0 bg-cover bg-center" 
            style={{ backgroundImage: 'url("/lovable-uploads/d208fa5b-4e06-412e-a8ca-a2d3ef63eb13.png")' }}
          >
            {/* Dark overlay for better text readability */}
            <div className="absolute inset-0 bg-black/40"></div>
            
            {/* Photo credit */}
            <div className="absolute bottom-2 right-4 text-xs text-white/80 z-10">
              Creator: JeremySeanWilliamsRIVERVOICES.ca<br />
              Copyright: RiverVoices.ca
            </div>
          </div>
          
          <div className="container mx-auto px-8 md:px-12 relative z-10">
            <div className="max-w-xl space-y-6 text-white">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">
                Our Mission to Keep People <span className="text-amber-400">Safe</span>
              </h1>
              
              <p className="body-lg text-white/80 text-pretty">
                EmergencyPlan was founded with a simple yet powerful mission: to make emergency 
                planning accessible to everyone and save lives through preparation.
              </p>
            </div>
          </div>
        </section>
        
        {/* What's With the Name Section */}
        <section className="py-12 bg-white">
          <div className="container px-4 md:px-6">
            <div className="max-w-[900px] mx-auto">
              <h2 className="heading-lg mb-6 opacity-0 animate-fade-up">What's With the Name?</h2>
              <div className="opacity-0 animate-fade-up" style={{ animationDelay: '100ms' }}>
                <p className="body-base mb-4">
                  Inspired by the adage that a small preventive measure can avert significant challenges, qoop is dedicated to empowering you with swift and effective emergency planning solutions. The 'q' signifies 'quick,' reflecting our focus on ease-of-use and quick plan turnaround, and 'oop' stands for 'ounce of prevention,' underscoring our dedication to proactive planning. With qoop, you're equipped to face unforeseen events with confidence and resilience.
                </p>
                <p className="body-base font-medium text-left mt-6">
                  <span className="font-bold">qoop</span><br />
                  <em>"Readiness on Tap"</em>
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Story Section */}
        <section className="py-12">
          <div className="container px-4 md:px-6">
            <div className="max-w-[900px] mx-auto">
              <h2 className="heading-lg mb-6 opacity-0 animate-fade-up">Our Story</h2>
              <div className="opacity-0 animate-fade-up" style={{ animationDelay: '100ms' }}>
                <p className="body-base mb-4">
                  Michael Budd and Rob Ranson have both devoted much of their adult lives to the pursuit of emergency preparedness. 
                  Michael resides on the Sunshine Coast in British Columbia—an area at high risk for natural disasters 
                  and potentially isolated from the mainland in a crisis. Over the years, he has accumulated extensive 
                  supplies and equipment, yet found that his planning often lagged behind—particularly when it came to 
                  maintaining a comprehensive, up-to-date emergency plan.
                </p>
                <p className="body-base mb-4">
                  Recent earthquakes (with a promise of an overdue "Cascadia Megathrust Earthquake") and the ever-present threat of wildfires in the region have underscored just how 
                  crucial effective planning can be. While assembling a detailed plan for his own family, Michael 
                  discovered firsthand how time-intensive and complex the process truly is—requiring considerable 
                  research, resource gathering, and careful organization. Recognizing that 60% of people in North America 
                  lack a written emergency plan, he became convinced there had to be a faster, more accessible way to 
                  guide others through the same process.
                </p>
                <p className="body-base">
                  Inspired by a passion for preparedness at both a personal and community level, Michael and Rob—both 
                  seasoned software and AI experts, as well as repeat business founders—joined forces to create this service. 
                  Their goal is to harness the power of technology to streamline the creation of robust emergency plans, 
                  ensuring that families and individuals are equipped with the knowledge, tools, and clear guidance they 
                  need to safeguard what matters most. Their combined expertise and longstanding friendship have shaped 
                  an innovative platform designed to make personal preparedness simpler, more effective, and accessible to everyone.
                </p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Team Section */}
        <section className="py-16">
          <div className="container px-4 md:px-6">
            <div className="text-center max-w-[800px] mx-auto mb-12 opacity-0 animate-fade-up">
              <h2 className="heading-xl mb-4">Our Team</h2>
              <p className="body-base text-muted-foreground">
                Meet the experts behind EmergencyPlan
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
              {team.map((member, index) => (
                <TeamCard 
                  key={index}
                  name={member.name}
                  role={member.role}
                  image={member.image}
                  index={index}
                />
              ))}
            </div>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="py-20">
          <div className="container px-4 md:px-6">
            <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 md:p-10 relative overflow-hidden">
              <div className="relative z-10 max-w-[600px] mx-auto text-center opacity-0 animate-fade-up">
                <h2 className="heading-lg mb-4">Join us in our mission</h2>
                <p className="body-base text-muted-foreground mb-8">
                  Help us make the world a safer place by being prepared for emergencies.
                  Start creating your emergency plan today.
                </p>
                <Link to="/signup">
                  <AnimatedButton size="lg">
                    Get Started
                  </AnimatedButton>
                </Link>
              </div>
            </div>
          </div>
        </section>
        
        {/* Footer */}
        <footer className="py-12 bg-muted/30 border-t border-border">
          <div className="container px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div className="col-span-1 md:col-span-2">
                <div className="mb-4">
                  <Logo />
                </div>
                <Separator className="mb-4" />
                <h3 className="text-lg font-bold mb-4">Readiness on Tap</h3>
                <p className="text-muted-foreground mb-4 max-w-[360px]">
                  AI-powered emergency planning for families and businesses. 
                  Stay prepared for any situation.
                </p>
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-4">Pages</h4>
                <ul className="space-y-2">
                  <li><Link to="/" className="text-sm text-muted-foreground hover:text-foreground">Home</Link></li>
                  <li><Link to="/features" className="text-sm text-muted-foreground hover:text-foreground">Features</Link></li>
                  <li><Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground">Pricing</Link></li>
                  <li><Link to="/about" className="text-sm text-muted-foreground hover:text-foreground">About</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-4">Legal</h4>
                <ul className="space-y-2">
                  <li><Link to="/privacy" className="text-sm text-muted-foreground hover:text-foreground">Privacy Policy</Link></li>
                  <li><Link to="/terms" className="text-sm text-muted-foreground hover:text-foreground">Terms of Service</Link></li>
                </ul>
              </div>
            </div>
            <div className="mt-12 pt-8 border-t border-border">
              <p className="text-sm text-muted-foreground text-center">
                © {new Date().getFullYear()} Readiness on Tap. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </PageTransition>
  );
};

export default About;
